import { useState } from 'react';
import { Text, Spinner, Button, makeStyles, tokens } from '@fluentui/react-components';
import { Folder24Regular, ArrowDownload24Regular, ChevronLeft24Regular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { fetchTenantDocuments, fetchDocumentDownloadUrl, type DocumentEntityType, type DocumentItem } from '../api/document.api';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { KpiCard } from '../components/shared/KpiCard';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useTenantStore } from '../store/tenant.store';
import { useRanchoStore } from '../store/rancho.store';
import { PageHeader } from '../components/shared/PageHeader';

/**
 * Fase C — "Documentos" del portal: repositorio transversal agrupado por
 * carpeta (tipo de entidad dueña del documento). Backend M12-3 ya existía por
 * entidad puntual; el listado tenant-wide se agregó junto con esta pantalla
 * (`GET /api/:tenantId/documents`, ver spec 2026-08-12-fase-c-pantallas-nuevas.md).
 */

const useStyles = makeStyles({
  root:   { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  grid:   { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalM },
  folder: {
    display: 'flex', flexDirection: 'column', gap: '6px',
    width: '180px', padding: '16px 18px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': { border: `1px solid ${tokens.colorBrandStroke1}` },
  },
  folderCount: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  backRow: { display: 'flex', alignItems: 'center', gap: '6px' },
});

const ENTITY_TYPES: DocumentEntityType[] = ['SHIPMENT', 'INVOICE', 'MODULE', 'PURCHASE_ORDER', 'OTHER'];

export function DocumentsPage() {
  const { formatDate } = useLocaleSettings();
  const s = useStyles();
  const t = useT();
  const { displayName } = useTenantStore();
  const documentsSubtitle = displayName
    ? t.documents.subtitleWithCompany.replace('{company}', displayName)
    : t.documents.subtitle;
  const [openFolder, setOpenFolder] = useState<DocumentEntityType | null>(null);

  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const { data: allDocs = [], isLoading } = useQuery({
    queryKey: ['tenant-documents', activeRanchoId],
    queryFn: () => fetchTenantDocuments(undefined, activeRanchoId),
  });

  function folderLabel(entityType: DocumentEntityType): string {
    return t.documents[`folder${entityType}` as keyof typeof t.documents] as string;
  }

  async function handleDownload(doc: DocumentItem) {
    const url = await fetchDocumentDownloadUrl(doc.s3Key);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  const columns: DataTableColumn<DocumentItem>[] = [
    { key: 'docType', header: t.documents.colType, width: 1.4 },
    { key: 'createdAt', header: t.documents.colDate, render: d => formatDate(d.createdAt.slice(0, 10)) },
    { key: 'generatedBy', header: t.documents.colGeneratedBy },
    {
      key: 'actions', header: t.common.actions, align: 'right',
      render: d => (
        <Button size="small" icon={<ArrowDownload24Regular />} onClick={() => handleDownload(d)}>
          {t.documents.download}
        </Button>
      ),
    },
  ];

  if (openFolder) {
    const docsInFolder = allDocs.filter(d => d.entityType === openFolder);
    return (
      <div className={s.root}>
        <div className={s.backRow}>
          <Button appearance="subtle" icon={<ChevronLeft24Regular />} onClick={() => setOpenFolder(null)}>
            {t.documents.back}
          </Button>
        </div>
        <h2 style={{ margin: 0 }}>{folderLabel(openFolder)}</h2>
        <DataTable
          columns={columns}
          rows={docsInFolder}
          rowKey={d => d.documentId}
          isLoading={isLoading}
          emptyMessage={t.documents.noDocuments}
        />
      </div>
    );
  }

  // KPIs derivados de allDocs — sin endpoint nuevo, todo client-side sobre lo ya cargado.
  const folderCounts = ENTITY_TYPES.map(entityType => ({
    entityType,
    count: allDocs.filter(d => d.entityType === entityType).length,
  }));
  const topFolder = folderCounts.reduce((max, f) => (f.count > max.count ? f : max), folderCounts[0]);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const last30dCount = allDocs.filter(d => d.createdAt >= thirtyDaysAgo).length;
  const distinctDocTypes = new Set(allDocs.map(d => d.docType)).size;

  const recentDocs = [...allDocs]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 8);

  return (
    <div className={s.root}>
      <PageHeader title={t.documents.title} subtitle={documentsSubtitle} />

      {isLoading && <Spinner size="small" label={t.documents.loading} />}
      {!isLoading && allDocs.length === 0 && <Text size={200}>{t.documents.noDocuments}</Text>}

      {!isLoading && allDocs.length > 0 && (
        <div className={s.kpiRow}>
          <KpiCard label={t.documents.kpiTotal} value={allDocs.length} />
          <KpiCard label={t.documents.kpiTopFolder} value={folderLabel(topFolder.entityType)} sub={String(topFolder.count)} />
          <KpiCard label={t.documents.kpiLast30d} value={last30dCount} />
          <KpiCard label={t.documents.kpiDocTypes} value={distinctDocTypes} />
        </div>
      )}

      <div className={s.grid}>
        {ENTITY_TYPES.map(entityType => {
          const count = allDocs.filter(d => d.entityType === entityType).length;
          return (
            <div key={entityType} className={s.folder} onClick={() => setOpenFolder(entityType)}>
              <Folder24Regular />
              <Text weight="semibold">{folderLabel(entityType)}</Text>
              <span className={s.folderCount}>{count} {t.documents.docCountSuffix}</span>
            </div>
          );
        })}
      </div>

      {recentDocs.length > 0 && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.documents.recentTitle}</span>
          <DataTable
            columns={columns}
            rows={recentDocs}
            rowKey={d => d.documentId}
            emptyMessage={t.documents.noDocuments}
          />
        </div>
      )}
    </div>
  );
}
